import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createNote,
  updateNote,
  deleteNote,
  togglePinNote,
  toggleArchiveNote,
  convertNoteToTask,
} from '../services/notesApi';
import { noteKeys } from './useNotes';
import { taskKeys } from './useTasks';
import type { CreateNoteRequest, UpdateNoteRequest } from '../types/note';

/**
 * Hook for note mutations (create, update, delete, pin, archive, convert)
 */
export const useNoteMutations = () => {
  const queryClient = useQueryClient();

  // Invalidate all note queries
  const invalidateNotes = () => {
    queryClient.invalidateQueries({ queryKey: noteKeys.all });
  };

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (data: CreateNoteRequest) => createNote(data),
    onSuccess: () => {
      invalidateNotes();
    },
  });

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNoteRequest }) =>
      updateNote(id, data),
    onSuccess: () => {
      invalidateNotes();
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: (id: string) => deleteNote(id),
    onSuccess: () => {
      invalidateNotes();
    },
  });

  // Pin note mutation
  const pinNoteMutation = useMutation({
    mutationFn: (id: string) => togglePinNote(id),
    onSuccess: () => {
      invalidateNotes();
    },
  });

  // Archive note mutation
  const archiveNoteMutation = useMutation({
    mutationFn: (id: string) => toggleArchiveNote(id),
    onSuccess: () => {
      invalidateNotes();
    },
  });

  // Convert to task mutation
  const convertToTaskMutation = useMutation({
    mutationFn: (id: string) => convertNoteToTask(id),
    onSuccess: () => {
      invalidateNotes();
      // Also invalidate tasks since we created a new one
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });

  return {
    createNote: createNoteMutation,
    updateNote: updateNoteMutation,
    deleteNote: deleteNoteMutation,
    pinNote: pinNoteMutation,
    archiveNote: archiveNoteMutation,
    convertToTask: convertToTaskMutation,
    isLoading:
      createNoteMutation.isPending ||
      updateNoteMutation.isPending ||
      deleteNoteMutation.isPending ||
      pinNoteMutation.isPending ||
      archiveNoteMutation.isPending ||
      convertToTaskMutation.isPending,
  };
};

export default useNoteMutations;
